import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FilterListRounded,
  MoreVertRounded,
  SearchRounded,
  CloseRounded,
  AddRounded,
  ReceiptRounded,
  DriveFileRenameOutlineRounded,
  DeleteRounded,
  DownloadRounded,
  SendRounded,
  VisibilityRounded,
  AttachMoneyRounded,
  DescriptionRounded,
  CalendarTodayRounded,
  PersonRounded,
  BusinessRounded,
  ChatBubbleOutlineRounded,
  StickyNote2Outlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDialog } from "../../../components/DialogContext";
import { useAccess } from "../../../components/Auth/AccessProvider";
import { backend_api } from "../../../static/server";
import {
  getAllBilles,
  updateStatus,
} from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { deleteCategories } from "../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import EditBillForm from "../../../components/LawyerCompo/Billing/EditBillForm";
import RecordPaymentFrom from "../../../components/LawyerCompo/Billing/RecordPaymentFrom";
import ActionMenuForm from "../../../components/LawyerCompo/Billing/ActionMenuForm";
import styles, {
  formatteDateDay,
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../styles/styles";
import DataTable from "../../../components/DataTable";
import { DataGridNoData } from "../../../static/staticdata";
import BillPreview from "../../../components/LawyerCompo/Billing/BillPreview";
import ContactApiField from "../../../components/LawyerCompo/Settings/FieldsUi/ContactApiField";
import FilterDrawer from "../../../components/FilterDrawer";
import DateRangeFilter from "../../../components/DateRangeFilter";
import NoBills from "../../../assets/no-bills.webp";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrencySymbol } from "../Settings/CurrencySetForm";
import BillGenerateForm from "../../../components/LawyerCompo/Billing/BillGenrateForm";
import axios from "axios";
import { debounceAsync } from "../../../utils/apiDebounce";
import CreateCommentForm from "../../../components/LawyerCompo/Dashboard/CreateCommentForm";
import RecordNotesPanel from "../../../components/LawyerCompo/Dashboard/RecordNotesPanel";
import { getModuleMeta } from "../../../components/LawyerCompo/Dashboard/commentActionUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function to generate invoice number
export const generateInvoiceNumber = (id) => {
  if (id === undefined || id === null) return "";
  return id < 100 ? `INV-${id.toString().padStart(3, "0")}` : `INV-${id}`;
};

const billFilters = [
  { value: "all", label: "All", color: "#9E9E9E" },
  { value: "draft", label: "Draft", color: "#757575" },
  { value: "pending", label: "Pending Approval", color: "#FF9800" },
  { value: "unpaid", label: "Unpaid", color: "#F44336" },
  { value: "paid", label: "Paid", color: "#4CAF50" },
  { value: "overdue", label: "Overdue", color: "#FF5722" },
  { value: "partial_paid", label: "Partially Paid", color: "#FFA000" },
];

const BillingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showDialog } = useDialog();
  const { hasAccess } = useAccess();
  const dispatch = useDispatch();

  // Redux selectors
  const { token, teamMemberLawyerId } = useSelector((data) => data.user);
  const { allBilles, loading } = useSelector((state) => state.billing);
  const { filteredContacts } = useSelector((state) => state.contact);
  const { pageSize, rowCount, count, nextPage, previousPage } = useSelector(
    (state) => state.billing.pagination,
  );

  // Local state
  const [page, setPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(pageSize || 10);
  const [searchBill, setSearchBill] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteBill, setDeleteBill] = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null,
    rowData: null,
  });
  const [activeTab, setActiveTab] = useState("all");
  const [filterAnchor, setFilterAnchor] = useState(null);

  // Filter states
  const [activeFilters, setActiveFilters] = useState({
    client: null,
    status: [],
    matter: null,
    amount_range: { min: null, max: null },
  });
  const [dateFilters, setDateFilters] = useState({
    due_date__gte: null,
    due_date__lte: null,
    issue_date__gte: null,
    issue_date__lte: null,
    created_at__gte: null,
    created_at__lte: null,
  });
  const [selectedDateType, setSelectedDateType] = useState("");
  const [selectedSpecificDate, setSelectedSpecificDate] = useState(null);
  const [openTrustFundForm, setOpenTrustFundForm] = useState(false);

  // Trust fund form
  const {
    control: trustFundControl,
    handleSubmit: handleTrustFundSubmit,
    formState: { errors: trustFundErrors },
  } = useForm({
    defaultValues: {
      issue_date: null,
      due_date: null,
      client: [],
      balance_type: "client",
    },
  });

  // Filter options (static)
  const filterOptions = {
    status: billFilters.filter((f) => f.value !== "all"),
    dateTypeOptions: [
      { value: "due_date", label: "Due Date" },
      { value: "issue_date", label: "Issue Date" },
      { value: "created_at", label: "Created Date" },
    ],
  };

  // Build query string based on current filters
  const queryString = useMemo(() => {
    const params = [];

    // Today's date for overdue filter
    const today = dayjs().format("YYYY-MM-DD");

    // Tab filter - special handling for overdue
    if (activeTab !== "all") {
      if (activeTab === "overdue") {
        params.push(`status=unpaid`);
        params.push(`due_date__lt=${today}`);
      } else {
        params.push(`status=${activeTab}`);
      }
    }

    // Client filter
    if (activeFilters.client) {
      params.push(`case__case_related_person=${activeFilters.client}`);
    }

    // Status filters from drawer - only if not on overdue tab
    if (activeFilters.status.length > 0 && activeTab !== "overdue") {
      params.push(`status=${activeFilters.status.join(",")}`);
    }

    // Matter filter
    if (activeFilters.matter) {
      params.push(`case=${activeFilters.matter}`);
    }

    // Amount range filters
    if (activeFilters.amount_range.min !== null) {
      params.push(`total_amount__gte=${activeFilters.amount_range.min}`);
    }
    if (activeFilters.amount_range.max !== null) {
      params.push(`total_amount__lte=${activeFilters.amount_range.max}`);
    }

    // Date range filters
    if (dateFilters.due_date__gte) {
      params.push(`due_date__gte=${dateFilters.due_date__gte}`);
    }
    if (dateFilters.due_date__lte) {
      params.push(`due_date__lte=${dateFilters.due_date__lte}`);
    }
    if (dateFilters.issue_date__gte) {
      params.push(`issue_date__gte=${dateFilters.issue_date__gte}`);
    }
    if (dateFilters.issue_date__lte) {
      params.push(`issue_date__lte=${dateFilters.issue_date__lte}`);
    }
    if (dateFilters.created_at__gte) {
      params.push(`created_at__gte=${dateFilters.created_at__gte}`);
    }
    if (dateFilters.created_at__lte) {
      params.push(`created_at__lte=${dateFilters.created_at__lte}`);
    }

    // Specific date filter
    if (selectedDateType && selectedSpecificDate) {
      const formattedDate = dayjs(selectedSpecificDate).format("YYYY-MM-DD");
      params.push(`${selectedDateType}=${formattedDate}`);
    }

    // Search filter (no debounce here; we use debouncedSearch later)
    if (searchBill) {
      params.push(`search=${encodeURIComponent(searchBill)}`);
    }

    return params.length > 0 ? `?${params.join("&")}` : "";
  }, [
    activeTab,
    activeFilters,
    dateFilters,
    selectedDateType,
    selectedSpecificDate,
    searchBill, // using raw searchBill; we'll debounce the fetch instead
  ]);

  // Memoized fetch function
  const fetchBills = useCallback(async () => {
    const url = `${backend_api}firm-case/bill/${queryString}`;
    await dispatch(getAllBilles({ url, token, page, pageSize: localPageSize }));
  }, [dispatch, token, page, localPageSize, queryString]);

  // Debounced API call using your debounceAsync utility
  useEffect(() => {
    // Create a unique key based on all dependencies that should trigger a fetch
    const key = JSON.stringify({ page, pageSize: localPageSize, queryString });

    debounceAsync(key, fetchBills, 500).catch((error) => {
      console.error("Debounced fetch failed:", error);
      toast.error("Failed to fetch bills");
    });

    // No cleanup needed; debounceAsync manages its own timers
  }, [page, localPageSize, queryString, fetchBills]);

  // Handle trust request URL param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openTrustRequest") !== "true") return;

    setOpenTrustFundForm(true);
    params.delete("openTrustRequest");

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  // Filter handlers with shallow equality checks
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setPage(1);
    }
  };

  const handleClientFilter = (client) => {
    const newClient = client?.id || null;
    setActiveFilters((prev) => {
      if (prev.client === newClient) return prev;
      return { ...prev, client: newClient };
    });
    setPage(1);
  };

  const handleStatusFilter = (status) => {
    setActiveFilters((prev) => {
      const newStatuses = prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status];
      // Shallow compare to avoid unnecessary updates
      if (
        newStatuses.length === prev.status.length &&
        newStatuses.every((s) => prev.status.includes(s))
      ) {
        return prev;
      }
      return { ...prev, status: newStatuses };
    });
    setPage(1);
  };

  const handleAmountRangeChange = (min, max) => {
    setActiveFilters((prev) => {
      if (prev.amount_range.min === min && prev.amount_range.max === max)
        return prev;
      return { ...prev, amount_range: { min, max } };
    });
    setPage(1);
  };

  const handleDateRangeChange = (range, dateType) => {
    setDateFilters((prev) => {
      let updated = { ...prev };
      if (dateType === "due_date") {
        if (
          prev.due_date__gte === range.due_date__gte &&
          prev.due_date__lte === range.due_date__lte
        ) {
          return prev;
        }
        updated.due_date__gte = range.due_date__gte || null;
        updated.due_date__lte = range.due_date__lte || null;
      } else if (dateType === "issue_date") {
        if (
          prev.issue_date__gte === range.issue_date__gte &&
          prev.issue_date__lte === range.issue_date__lte
        ) {
          return prev;
        }
        updated.issue_date__gte = range.issue_date__gte || null;
        updated.issue_date__lte = range.issue_date__lte || null;
      } else if (dateType === "created_at") {
        if (
          prev.created_at__gte === range.created_at__gte &&
          prev.created_at__lte === range.created_at__lte
        ) {
          return prev;
        }
        updated.created_at__gte = range.created_at__gte || null;
        updated.created_at__lte = range.created_at__lte || null;
      }
      return updated;
    });
    setPage(1);
  };

  const handleDateTypeChange = (event) => {
    const newType = event.target.value;
    if (newType !== selectedDateType) {
      setSelectedDateType(newType);
      setSelectedSpecificDate(null);
      setPage(1);
    }
  };

  const handleSpecificDateChange = (date) => {
    // Compare by string value to avoid unnecessary updates
    const newDateStr = date ? date.format("YYYY-MM-DD") : null;
    const oldDateStr = selectedSpecificDate
      ? dayjs(selectedSpecificDate).format("YYYY-MM-DD")
      : null;
    if (newDateStr !== oldDateStr) {
      setSelectedSpecificDate(date);
      setPage(1);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    setFilterAnchor(null);
  };

  const handleResetFilters = () => {
    setActiveTab("all");
    setActiveFilters({
      client: null,
      status: [],
      matter: null,
      amount_range: { min: null, max: null },
    });
    setDateFilters({
      due_date__gte: null,
      due_date__lte: null,
      issue_date__gte: null,
      issue_date__lte: null,
      created_at__gte: null,
      created_at__lte: null,
    });
    setSelectedDateType("");
    setSelectedSpecificDate(null);
    setSearchBill("");
    setPage(1);
    setFilterAnchor(null);
  };

  // Other handlers (delete, preview, etc.) unchanged
  const handleOpenDeleteDialog = (bill) => {
    setDeleteBill(bill);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteBill(null);
  };

  const handleDeleteBill = async () => {
    if (!deleteBill) return;
    setDeleteLoad(true);
    try {
      await dispatch(
        deleteCategories({
          url: `${backend_api}firm-case/bill/${deleteBill.id}/`,
          token,
        }),
      ).unwrap();
      toast.success("Invoice deleted successfully");
      // Deletion should update Redux state; no need to manually refetch
    } catch (error) {
      toast.error("Failed to delete invoice");
    } finally {
      setDeleteLoad(false);
      handleCloseDeleteDialog();
    }
  };

  const handleOpenPreviewDialog = (bill) => {
    showDialog(<BillPreview entryData={bill} />, {
      dialogTitle: bill.invoice_num,
      width: "50rem",
    });
  };

  const handleDownloadBill = (bill) => {
    showDialog(
      <BillPreview entryData={bill} autoDownload autoCloseOnDownload />,
      {
        dialogTitle: bill.invoice_num,
        width: "50rem",
      },
    );
  };

  const handleEditBill = (bill) => {
    showDialog(<EditBillForm editBillData={bill} />, {
      dialogTitle: "Edit Invoice",
      fullScreen: true,
      fullWidth: true,
      width: "100%",
    });
  };

  const handleAddBillComment = (bill) => {
    const commentMeta = getModuleMeta(bill, "bill");

    if (!commentMeta.object_id) {
      toast.error("Unable to open comment form for this bill.");
      return;
    }

    showDialog(<CreateCommentForm {...commentMeta} />, {
      dialogTitle: "Add Comment",
    });
  };

  const handleViewBillNotes = (bill) => {
    const noteMeta = getModuleMeta(bill, "bill");

    if (!noteMeta.object_id) {
      toast.error("Unable to open notes for this invoice.");
      return;
    }

    showDialog(
      <RecordNotesPanel
        module={noteMeta.module}
        objectId={noteMeta.object_id}
        objectName={noteMeta.object_name}
        variant="dialog"
      />,
      {
        dialogTitle: "Invoice Notes",
        width: "48rem",
      },
    );
  };

  const handleRecordPayment = (bill) => {
    const selectedBill = bill?.full_data || bill || null;
    showDialog(
      <RecordPaymentFrom
        entryData={selectedBill}
        onSuccess={() => {}} // optional refresh; rely on Redux updates
        billStatus={selectedBill?.status}
      />,
      {
        dialogTitle: "Record Payment",
        width: "40rem",
      },
    );
  };

  const handleSubmitForApproval = (bill) => {
    const body = {
      case: bill?.full_data?.case?.id,
      expenses: bill?.full_data?.expenses?.map((exp) => exp.id) || [],
      status: "pending",
      due_date: bill?.full_data?.due_date,
      firm: bill?.full_data?.firm?.id,
    };
    showDialog(
      <ActionMenuForm
        form_type="submitAproval"
        body={body}
        BillId={bill.id}
        fetchBills={() => {}} // optional
        setActiveFilter={setActiveTab}
      />,
      { dialogTitle: "Ask for approval" },
    );
  };

  const handleApproveInvoice = (bill) => {
    const body = {
      case: bill?.full_data?.case?.id,
      expenses: bill?.full_data?.expenses?.map((exp) => exp.id) || [],
      status: "unpaid",
      due_date: bill?.full_data?.due_date,
      firm: bill?.full_data?.firm?.id,
    };
    dispatch(
      updateStatus({
        id: bill.id,
        data: body,
        token,
      }),
    ).then(() => {
      setActiveTab("unpaid");
      toast.success("Invoice approved successfully");
    });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = useCallback((newPageSize) => {
    setLocalPageSize(newPageSize);
    setPage(1);
  }, []);

  const handleActionMenuOpen = (event, row) => {
    setActionMenu({ anchorEl: event.currentTarget, rowData: row });
  };

  const handleActionMenuClose = () => {
    setActionMenu({ anchorEl: null, rowData: null });
  };

  // Memoized active filter chips
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (activeTab !== "all") {
      const tabOption = billFilters.find((tab) => tab.value === activeTab);
      if (tabOption) {
        chips.push(
          <Chip
            key="tab-filter"
            label={`Status: ${tabOption.label}`}
            size="small"
            onDelete={() => handleTabChange("all")}
            sx={{
              backgroundColor: `${tabOption.color}15`,
              color: tabOption.color,
              border: `1px solid ${tabOption.color}30`,
              maxWidth: 160,
            }}
          />,
        );
      }
    }
    activeFilters.status.forEach((status) => {
      const option = filterOptions.status.find((opt) => opt.value === status);
      if (option) {
        chips.push(
          <Chip
            key={`status-${status}`}
            label={`Status: ${option.label}`}
            size="small"
            onDelete={() => handleStatusFilter(status)}
            sx={{
              backgroundColor: `${option.color}15`,
              color: option.color,
              border: `1px solid ${option.color}30`,
              maxWidth: 160,
            }}
          />,
        );
      }
    });
    if (activeFilters.client) {
      const client = filteredContacts?.find(
        (c) => c.id === activeFilters.client,
      );
      if (client) {
        const clientName =
          client.first_name || client.last_name
            ? `${client.first_name || ""} ${client.last_name || ""}`.trim()
            : client.company_name || "Selected Client";
        chips.push(
          <Chip
            key="client-filter"
            label={`Client: ${clientName}`}
            size="small"
            onDelete={() => handleClientFilter(null)}
            sx={{
              backgroundColor: "#2196F315",
              color: "#2196F3",
              border: "1px solid #2196F330",
              maxWidth: 160,
            }}
          />,
        );
      }
    }
    if (
      activeFilters.amount_range.min !== null ||
      activeFilters.amount_range.max !== null
    ) {
      const min = activeFilters.amount_range.min || "0";
      const max = activeFilters.amount_range.max || "∞";
      chips.push(
        <Chip
          key="amount-range"
          label={`Amount: $${min} - $${max}`}
          size="small"
          onDelete={() => handleAmountRangeChange(null, null)}
          sx={{
            backgroundColor: "#FF980015",
            color: "#FF9800",
            border: "1px solid #FF980030",
            maxWidth: 200,
          }}
        />,
      );
    }
    if (dateFilters.due_date__gte && dateFilters.due_date__lte) {
      chips.push(
        <Chip
          key="due-date-range"
          label={`Due: ${dateFilters.due_date__gte} to ${dateFilters.due_date__lte}`}
          size="small"
          onDelete={() => {
            setDateFilters((prev) => ({
              ...prev,
              due_date__gte: null,
              due_date__lte: null,
            }));
            setPage(1);
          }}
          sx={{
            backgroundColor: "#F4433615",
            color: "#F44336",
            border: "1px solid #F4433630",
            maxWidth: 200,
          }}
        />,
      );
    }
    if (dateFilters.issue_date__gte && dateFilters.issue_date__lte) {
      chips.push(
        <Chip
          key="issue-date-range"
          label={`Issue: ${dateFilters.issue_date__gte} to ${dateFilters.issue_date__lte}`}
          size="small"
          onDelete={() => {
            setDateFilters((prev) => ({
              ...prev,
              issue_date__gte: null,
              issue_date__lte: null,
            }));
            setPage(1);
          }}
          sx={{
            backgroundColor: "#2196F315",
            color: "#2196F3",
            border: "1px solid #2196F330",
            maxWidth: 200,
          }}
        />,
      );
    }
    if (selectedDateType && selectedSpecificDate) {
      const dateTypeLabel = filterOptions.dateTypeOptions.find(
        (opt) => opt.value === selectedDateType,
      )?.label;
      const displayDate = dayjs(selectedSpecificDate).format("YYYY-MM-DD");
      chips.push(
        <Chip
          key={`specific-date-${selectedDateType}`}
          label={`${dateTypeLabel}: ${displayDate}`}
          size="small"
          onDelete={() => {
            setSelectedDateType("");
            setSelectedSpecificDate(null);
            setPage(1);
          }}
          sx={{
            backgroundColor: "#9C27B015",
            color: "#9C27B0",
            border: "1px solid #9C27B030",
            maxWidth: 200,
          }}
        />,
      );
    }
    if (chips.length > 0) {
      chips.unshift(
        <Typography
          key="active-filters-label"
          variant="body2"
          sx={{
            fontWeight: 600,
            flexShrink: 0,
            color: "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          Active Filters:
        </Typography>,
      );
    }
    return chips;
  }, [
    activeTab,
    activeFilters,
    dateFilters,
    selectedDateType,
    selectedSpecificDate,
    filteredContacts,
  ]);

  // Columns (memoized)
  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => handleActionMenuOpen(e, params.row)}
              >
                <MoreVertRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={actionMenu.anchorEl}
              open={Boolean(actionMenu.anchorEl)}
              onClose={handleActionMenuClose}
              slotProps={{ root: { sx: { ".MuiList-root": { padding: 0 } } } }}
            >
              <MenuItem
                onClick={() => {
                  handleOpenPreviewDialog(actionMenu.rowData);
                  handleActionMenuClose();
                }}
                sx={{
                  py: 1,
                  px: 2,
                  gap: 1.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <VisibilityRounded
                  sx={{ fontSize: 18, color: "action.active" }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Preview
                  </Typography>
                </Box>
              </MenuItem>
               <MenuItem
                  onClick={() => {
                   handleEditBill(actionMenu?.rowData?.full_data);
                   handleActionMenuClose();
                 }}
                sx={{
                  py: 1,
                  px: 2,
                  gap: 1.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <DriveFileRenameOutlineRounded
                  sx={{ fontSize: 18, color: "action.active" }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Edit
                  </Typography>
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleAddBillComment(
                      actionMenu?.rowData?.full_data || actionMenu?.rowData,
                    );
                    handleActionMenuClose();
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <ChatBubbleOutlineRounded
                    sx={{ fontSize: 18, color: "action.active" }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Add Comment
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleViewBillNotes(
                      actionMenu?.rowData?.full_data || actionMenu?.rowData,
                    );
                    handleActionMenuClose();
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <StickyNote2Outlined
                    sx={{ fontSize: 18, color: "action.active" }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      View Notes
                    </Typography>
                  </Box>
                </MenuItem>
                {(actionMenu.rowData?.status === "pending" ||
                  actionMenu.rowData?.status === "draft") && (
                <MenuItem
                  onClick={() => {
                    handleApproveInvoice(actionMenu.rowData);
                    handleActionMenuClose();
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <AttachMoneyRounded
                    sx={{ fontSize: 18, color: "action.active" }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Approve Invoice
                    </Typography>
                  </Box>
                </MenuItem>
              )}
              {["unpaid", "partial_paid"].includes(
                actionMenu.rowData?.status,
              ) && (
                <MenuItem
                  onClick={() => {
                    handleRecordPayment(actionMenu.rowData);
                    handleActionMenuClose();
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <ReceiptRounded
                    sx={{ fontSize: 18, color: "action.active" }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Record Payment
                    </Typography>
                  </Box>
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  handleDownloadBill(actionMenu.rowData);
                  handleActionMenuClose();
                }}
                sx={{
                  py: 1,
                  px: 2,
                  gap: 1.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <DownloadRounded
                  sx={{ fontSize: 18, color: "action.active" }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Download PDF
                  </Typography>
                </Box>
              </MenuItem>
              {actionMenu.rowData?.status !== "paid" && (
                <MenuItem
                  onClick={() => {
                    handleOpenDeleteDialog(actionMenu.rowData);
                    handleActionMenuClose();
                  }}
                  sx={{
                    borderTop: "1px solid #e5e5e5",
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "error.main",
                      color: "error.contrastText",
                      "& .MuiSvgIcon-root": { color: "error.contrastText" },
                    },
                  }}
                >
                  <DeleteRounded sx={{ fontSize: 18, color: "error.main" }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Delete
                    </Typography>
                  </Box>
                </MenuItem>
              )}
            </Menu>
          </Box>
        ),
      },
      {
        field: "invoice_num",
        headerName: "Invoice #",
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "primary.main",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => handleOpenPreviewDialog(params.row)}
          >
            {params.row.invoice_num}
          </Typography>
        ),
      },
      {
        field: "client",
        headerName: "Client",
        flex: 2,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {params.row.client_type === "company" ? (
              <BusinessRounded sx={{ fontSize: "0.9rem", color: "#2e77f5" }} />
            ) : (
              <PersonRounded sx={{ fontSize: "0.9rem", color: "#bf3fe1" }} />
            )}
            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
              {params.row.client}
            </Typography>
          </Box>
        ),
      },
      {
        field: "case_name",
        headerName: "Matter",
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "text.primary",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              mb: 0.5,
              "&:hover": { cursor: "pointer", textDecoration: "underline" },
            }}
            title={params.row.client_ref}
            onClick={() => {
              navigate(
                `/dashboard/matter/${params.row.client_ref}/${params.row.case_id}/`,
              );
            }}
          >
            Matter: {params.row.case_name}
          </Typography>
        ),
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 90,
        renderCell: (params) => (
          <Chip
            label={params.row.currency}
            size="small"
            variant="outlined"
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: "#f0f0f0",
            }}
          />
        ),
      },
      {
        field: "total_amount",
        headerName: "Amount",
        width: 120,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.85rem", fontWeight: 600 }}
          >
            {getCurrencySymbol(params?.row?.currency)}{" "}
            {params.row.total_amount?.toFixed(2) || "0.00"}
          </Typography>
        ),
      },
      {
        field: "balance",
        headerName: "Balance",
        width: 120,
        renderCell: (params) => {
          const isPaid = params.row.status === "paid";
          const isOverdue = params.row.status === "overdue";
          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: isPaid
                  ? "success.main"
                  : isOverdue
                    ? "error.main"
                    : "warning.main",
                fontWeight: 600,
              }}
            >
              {getCurrencySymbol(params?.row?.currency)}{" "}
              {params.row.balance?.toFixed(2) || "0.00"}
            </Typography>
          );
        },
      },
      {
        field: "due_date",
        headerName: "Due Date",
        width: 120,
        renderCell: (params) => {
          const isOverdue =
            params.row.status === "overdue" ||
            (params.row.due_date && new Date(params.row.due_date) < new Date());
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayRounded
                sx={{
                  fontSize: "0.9rem",
                  color: isOverdue ? "error.main" : "text.secondary",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.85rem",
                  color: isOverdue ? "error.main" : "text.primary",
                  fontWeight: isOverdue ? 600 : 400,
                }}
              >
                {params.row.due_date}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => {
          const statusOption = billFilters.find(
            (opt) => opt.value === params.row.status,
          );
          return (
            <Chip
              label={statusOption?.label || params.row.status}
              size="small"
              sx={{
                backgroundColor: `${statusOption?.color || "#9E9E9E"}15`,
                color: statusOption?.color || "#9E9E9E",
                border: `1px solid ${statusOption?.color || "#9E9E9E"}30`,
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          );
        },
      },
      {
        field: "issue_date",
        headerName: "Issued",
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.issue_date}
          </Typography>
        ),
      },
    ],
    [actionMenu.anchorEl, actionMenu.rowData], // only re-render when menu changes
  );

  // Rows data (memoized)
  const rows = useMemo(() => {
    if (!Array.isArray(allBilles)) return [];
    return allBilles.map((item) => {
      const client = item?.case?.case_related_person;
      const clientType =
        client?.contact_type === "company" ? "company" : "person";
      return {
        id: item?.id ?? "-",
        invoice_num: item?.id ? generateInvoiceNumber(item?.id) : "-",
        client: client
          ? `${client?.first_name || ""} ${client?.last_name || ""}`.trim() ||
            client?.company_name ||
            "-"
          : "-",
        client_type: clientType,
        case_id: item?.case?.id ?? null,
        client_ref: item?.case?.client_ref ?? null,
        case_name: item?.case?.id
          ? generateUniqueMatterName(item?.case?.id, item?.case?.case_name)
          : "-",
        currency: item?.currency ?? "-",
        total_amount: parseFloat(item?.total_amount) || 0,
        balance: parseFloat(item?.balance) || 0,
        due_date: item?.due_date ? formatteDateDay(item.due_date) : "-",
        issue_date: item?.issue_date ? formatteDateDay(item.issue_date) : "-",
        status: item?.status ?? "-",
        full_data: item,
      };
    });
  }, [allBilles]);

  // Tab buttons (memoized)
  const extraBtn = useCallback(() => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          p: "0.2rem",
          borderRadius: "0.5rem",
        }}
      >
        {billFilters.map((filter) => (
          <Button
            key={filter.value}
            size="small"
            variant={activeTab === filter.value ? "contained" : "text"}
            sx={{
              textTransform: "capitalize",
              fontSize: "0.8rem",
              transition: "0.3s",
            }}
            onClick={() => handleTabChange(filter.value)}
          >
            {filter.label}
          </Button>
        ))}
      </Box>
    );
  }, [activeTab]);

  const isFilterApplied = useMemo(() => {
    return (
      activeTab !== "all" ||
      activeFilters.client !== null ||
      activeFilters.status.length > 0 ||
      activeFilters.matter !== null ||
      activeFilters.amount_range.min !== null ||
      activeFilters.amount_range.max !== null ||
      dateFilters.due_date__gte !== null ||
      dateFilters.due_date__lte !== null ||
      dateFilters.issue_date__gte !== null ||
      dateFilters.issue_date__lte !== null ||
      dateFilters.created_at__gte !== null ||
      dateFilters.created_at__lte !== null ||
      selectedDateType !== "" ||
      selectedSpecificDate !== null ||
      searchBill !== "" // use raw search for UI indicator
    );
  }, [
    activeTab,
    activeFilters,
    dateFilters,
    selectedDateType,
    selectedSpecificDate,
    searchBill,
  ]);

  const handleTrustFundSubmitForm = (data) => {
    toast.success("Trust request created successfully");
    setOpenTrustFundForm(false);
  };

  // New Bill button handler
  const handleNewBill = async () => {
    try {
      const response = await axios.get(
        `${backend_api}firm-case/expense/?bill_genrated=false&limit=50`,
        { headers: { Authorization: `Token ${token}` } },
      );
      const activities = response.data.results || [];
      if (activities.length === 0) {
        toast.info(
          "No billable activities found. Please create activities first.",
        );
        return;
      }
      const formattedActivities = activities.map((activity) => ({
        id: activity.id,
        type: activity.type,
        description: activity.description,
        matter_name: activity.case?.case_name || "Unknown Matter",
        case: activity.case?.id,
        full_data: activity,
        billable_amount: activity.total_payment || "0.00",
        rate: activity.type === "time" ? activity.hourly_rate : activity.rate,
        quantity: activity.type === "time" ? "1" : activity.quantity,
        currency: activity.currency || "USD",
      }));
      showDialog(<BillGenerateForm entryData={formattedActivities} />, {
        dialogTitle: "Create New Bill",
      });
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast.error("Failed to load activities. Please try again.");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "1rem",
        }}
      >
        <h3
          className={`${styles.title}`}
          style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}
        >
          Billing
        </h3>
        <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search by invoice #, client, or matter"
            value={searchBill}
            onChange={(e) => setSearchBill(e.target.value)}
            sx={{
              minWidth: "240px",
              fontSize: "0.8rem",
              "& .MuiOutlinedInput-root": {
                borderRadius: "32px",
                pr: 0.5,
                backgroundColor: "#f8f9fa",
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: 0.5,
                fontSize: "0.8rem",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchBill ? (
                    <IconButton
                      size="small"
                      onClick={() => setSearchBill("")}
                      sx={{
                        border: "0.1px solid #e5e5e5",
                        p: 0.5,
                        backgroundColor: "white",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      disabled
                      sx={{
                        border: "0.1px solid #e5e5e5",
                        p: 0.5,
                        backgroundColor: "white",
                      }}
                    >
                      <SearchRounded fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Tooltip title="Filter" placement="bottom" arrow>
              <IconButton
                id="filter-button"
                size="small"
                onClick={(e) => setFilterAnchor(e.currentTarget)}
                sx={{
                  border: "1px solid",
                  borderColor: isFilterApplied ? "primary.main" : "divider",
                  bgcolor: isFilterApplied ? "primary.main" : "transparent",
                  color: isFilterApplied ? "#fff" : "text.secondary",
                  position: "relative",
                  width: 32,
                  height: 32,
                  transition: "0.3s",
                  "&:hover": {
                    bgcolor: isFilterApplied ? "primary.dark" : "action.hover",
                  },
                }}
              >
                <FilterListRounded fontSize="small" />
                {isFilterApplied && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "error.main",
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", gap: "0.5rem" }}>
            {hasAccess("access_billing_record_payment") && (
              <Button
                id="access_billing_record_payment"
                variant="outlined"
                size="small"
                sx={{ textTransform: "capitalize" }}
                startIcon={<ReceiptRounded />}
                onClick={() =>
                  showDialog(
                    <RecordPaymentFrom
                      onSuccess={() => {}} // optional refresh
                      billStatus="unpaid"
                    />,
                    { dialogTitle: "Record Payment", width: "40rem" },
                  )
                }
              >
                Record Payment
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              sx={{ textTransform: "capitalize" }}
              startIcon={<AddRounded />}
              onClick={handleNewBill}
            >
              New Bill
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Data Table */}
      <DataTable
        extraBtn={extraBtn}
        columns={columns}
        rows={rows}
        CustomNoRowsOverlay={() => (
          <DataGridNoData imgSrc={NoBills} text="No invoices available" />
        )}
        loading={loading}
        page={page}
        setPage={setPage}
        pageSize={localPageSize}
        rowCount={rowCount}
        count={count}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        hasNextPage={nextPage !== null}
        hasPreviousPage={previousPage !== null}
        filterChips={activeFilterChips}
        height="calc(100vh - 9rem)"
      />

      {/* Delete Dialog (unchanged) */}
      <Dialog
        open={openDeleteDialog}
        TransitionComponent={Transition}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Delete Invoice Confirmation
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Are you sure you want to delete this invoice? This action cannot be
            undone.
          </Typography>
        </DialogTitle>
        {deleteBill && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                backgroundColor: "grey.50",
                borderRadius: 2,
                p: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Invoice Details:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "100px" }}
                  >
                    Invoice #:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {deleteBill.invoice_num}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "100px" }}
                  >
                    Client:
                  </Typography>
                  <Typography variant="body2">{deleteBill.client}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "100px" }}
                  >
                    Matter:
                  </Typography>
                  <Typography variant="body2">{deleteBill.case}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "100px" }}
                  >
                    Amount:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    ${deleteBill.total_amount?.toFixed(2) || "0.00"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "100px" }}
                  >
                    Status:
                  </Typography>
                  <Chip
                    label={deleteBill.status}
                    size="small"
                    sx={{
                      backgroundColor:
                        billFilters.find((s) => s.value === deleteBill.status)
                          ?.color + "15" || "#9E9E9E15",
                      color:
                        billFilters.find((s) => s.value === deleteBill.status)
                          ?.color || "#9E9E9E",
                      border: `1px solid ${billFilters.find((s) => s.value === deleteBill.status)?.color || "#9E9E9E"}30`,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "100px" }}
                  >
                    Due Date:
                  </Typography>
                  <Typography variant="body2">{deleteBill.due_date}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCloseDeleteDialog}
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={deleteLoad}
            size="small"
            variant="contained"
            color="error"
            onClick={handleDeleteBill}
            sx={{ textTransform: "capitalize" }}
          >
            Delete Invoice
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Trust Fund Request Dialog (unchanged) */}
      <Dialog
        open={openTrustFundForm}
        TransitionComponent={Transition}
        onClose={() => setOpenTrustFundForm(false)}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": pageScrollStyle }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "1rem",
            py: "0.5rem",
            bgcolor: "primary.main",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography variant="h6" color="white.main">
            New Trust Request
          </Typography>
          <IconButton
            onClick={() => setOpenTrustFundForm(false)}
            sx={{ color: "white.main" }}
          >
            <CloseRounded />
          </IconButton>
        </Box>
        <Box sx={{ p: 3 }}>
          <form onSubmit={handleTrustFundSubmit(handleTrustFundSubmitForm)}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Controller
                  name="issue_date"
                  control={trustFundControl}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Issue Date"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date ? date.format("YYYY-MM-DD") : null)
                      }
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  )}
                />
                <Controller
                  name="due_date"
                  control={trustFundControl}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Due Date"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) =>
                        field.onChange(date ? date.format("YYYY-MM-DD") : null)
                      }
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Controller
                  name="client"
                  control={trustFundControl}
                  render={({ field }) => (
                    <ContactApiField
                      name="client"
                      label="Client"
                      token={token}
                      apiUrl={`${backend_api}contact/retrieve/`}
                      isRequired={false}
                      placeholder="Select client"
                      returnFullData
                      onChange={(value) => field.onChange(value ? [value] : [])}
                      value={field.value?.[0] || null}
                    />
                  )}
                />
                <Controller
                  name="balance_type"
                  control={trustFundControl}
                  render={({ field }) => (
                    <Select {...field} size="small" fullWidth displayEmpty>
                      <MenuItem value="client">Client</MenuItem>
                      <MenuItem value="matter">Matter</MenuItem>
                    </Select>
                  )}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                20 Matters linked to client name
              </Typography>
              <Button
                variant="text"
                color="primary"
                startIcon={<AddRounded />}
                size="small"
                sx={{ alignSelf: "flex-start" }}
              >
                Add Matter
              </Button>
            </Box>
          </form>
        </Box>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            variant="outlined"
            onClick={() => setOpenTrustFundForm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTrustFundSubmit(handleTrustFundSubmitForm)}
          >
            Save Trust Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Drawer */}
      <FilterDrawer
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        anchorEl={filterAnchor}
        isFilterApplied={isFilterApplied}
        title="Invoice Filters"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
          {/* Client Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Client
            </Typography>
            <ContactApiField
              name="client_filter"
              label="Select Client"
              token={token}
              apiUrl={`${backend_api}contact/retrieve/`}
              isRequired={false}
              placeholder="Search for a client"
              returnFullData
              onChange={handleClientFilter}
              value={filteredContacts?.find(
                (c) => c.id === activeFilters.client,
              )}
            />
          </Box>

          {/* Status Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Status
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {filterOptions.status.map((status) => (
                <Chip
                  key={status.value}
                  label={status.label}
                  size="small"
                  clickable
                  color={
                    activeFilters.status.includes(status.value)
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleStatusFilter(status.value)}
                  variant={
                    activeFilters.status.includes(status.value)
                      ? "filled"
                      : "outlined"
                  }
                  sx={{
                    backgroundColor: activeFilters.status.includes(status.value)
                      ? `${status.color}15`
                      : undefined,
                    color: activeFilters.status.includes(status.value)
                      ? status.color
                      : undefined,
                    borderColor: activeFilters.status.includes(status.value)
                      ? status.color
                      : undefined,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Amount Range Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Amount
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                type="number"
                label="Min"
                value={activeFilters.amount_range.min || ""}
                onChange={(e) =>
                  handleAmountRangeChange(
                    e.target.value,
                    activeFilters.amount_range.max,
                  )
                }
                fullWidth
                InputProps={{ startAdornment: <Typography>$</Typography> }}
              />
              <TextField
                size="small"
                type="number"
                label="Max"
                value={activeFilters.amount_range.max || ""}
                onChange={(e) =>
                  handleAmountRangeChange(
                    activeFilters.amount_range.min,
                    e.target.value,
                  )
                }
                fullWidth
                InputProps={{ startAdornment: <Typography>$</Typography> }}
              />
            </Box>
          </Box>

          {/* Date Range Filters */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Date Range
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <DateRangeFilter
                label="Due Date Range"
                onDateRangeChange={(range) =>
                  handleDateRangeChange(range, "due_date")
                }
                dateType="due_date"
              />
              <DateRangeFilter
                label="Issue Date Range"
                onDateRangeChange={(range) =>
                  handleDateRangeChange(range, "issue_date")
                }
                dateType="issue_date"
              />
              <DateRangeFilter
                label="Created Date Range"
                onDateRangeChange={(range) =>
                  handleDateRangeChange(range, "created_at")
                }
                dateType="created_at"
              />
            </Box>
          </Box>

          {/* Specific Date Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Specific Date
            </Typography>
            <Select
              size="small"
              fullWidth
              value={selectedDateType}
              onChange={handleDateTypeChange}
              displayEmpty
            >
              <MenuItem value="">Select Date Type</MenuItem>
              {filterOptions.dateTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {selectedDateType && (
              <Box sx={{ mt: 1 }}>
                <DateCalendar
                  value={selectedSpecificDate}
                  onChange={handleSpecificDateChange}
                  sx={{ width: "100%" }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </FilterDrawer>
    </Box>
  );
};

export default React.memo(BillingPage);
